/* eslint-disable @typescript-eslint/no-shadow */
import { useState, useEffect } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { MdAddCircleOutline } from 'react-icons/md';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  AVAILABLE_WAREHOUSES_URL,
  SUGGESTED_ID_URL,
  SUPPLIER_GET,
} from '@constants';
import { useAuthHttp } from '@hooks';
import { Warehouse, WarehouseMatch } from '@views/WarehouseDetails/types';
import { Supplier } from '@views/SupplierMaster/supplierRecord';
import { FormPage } from '../../tailwindComponents/FormPage';
import { Input } from '../../tailwindComponents/Input';
import { Subheader } from '../../tailwindComponents/Subheader';
import { Textarea } from '../../tailwindComponents/Textarea';
import { Datepicker } from '../../tailwindComponents/Datepicker';
import { CreateInboundShipmentNoteFormValues } from './types';
import { Select } from '../../tailwindComponents/Select';
import { SkuFieldRow } from './SkuFieldRow';
import { CreateSupplierModal } from './CreateSupplierModal';

export const skuSchema = Yup.object().shape({
  palletising: Yup.object().shape({
    palletisingId: Yup.object().shape({
      label: Yup.string().required('Pallet is required'),
      value: Yup.string().required('Pallet is required'),
    }),
  }),
  skuId: Yup.string().required('SKU ID is required'),
  skuQuantity: Yup.string()
    .required('Quantity is required')
    .test(
      'min-if-present',
      'Must be at least 1',
      (value) => !value || Number(value) >= 1
    )
    .matches(/^\d*\.?\d*$/, 'Must be a valid number'),
});

const schema = Yup.object().shape({
  skuFields: Yup.array().of(skuSchema),
  shipmentNoteNumber: Yup.string().required('Shipment note number is required'),
  supplier: Yup.object()
    .shape({
      label: Yup.string().required(),
      value: Yup.string().required(),
    })
    .required('Supplier is required'),
  warehouse: Yup.object()
    .shape({
      label: Yup.string().required(),
      value: Yup.string().required(),
    })
    .required('Warehouse is required'),
  estimatedTimeOfArrival: Yup.string().required('ETA is required'),
  comment: Yup.string(),
});

export function CreateInboundShipmentNoteForm({
  onSubmit,
  isSubmitting,
  header,
  pages,
}: {
  onSubmit: (data: CreateInboundShipmentNoteFormValues) => void;
  isSubmitting: boolean;
  header: string;
  pages: { name: string; href: string; current: boolean }[];
}) {
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [isSupplierLoading, setIsSupplierLoading] = useState(false);
  const [warehouseOptions, setWarehouseOptions] = useState<Warehouse[]>([]);
  const [isAvailableWarehousesLoading, setIsAvailableWarehousesLoading] =
    useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(
    null
  );
  const [isCreateSupplierModalOpen, setIsCreateSupplierModalOpen] =
    useState(false);
  const authHttp = useAuthHttp();

  const methods = useForm({
    shouldUnregister: false,
    resolver: yupResolver(schema),
    mode: 'onChange',
    defaultValues: {
      skuFields: [
        {
          palletising: {
            palletisingId: {
              label: '',
              value: '',
            },
          },
          id: '',
          skuId: '',
          skuQuantity: '',
        },
      ],
      shipmentNoteNumber: '',
      supplier: null,
      supplierLocation: null,
      warehouse: null,
      estimatedTimeOfArrival: '',
      comment: '',
    } as CreateInboundShipmentNoteFormValues,
  });

  const { formState, control, setValue } = methods;

  useEffect(() => {
    async function fetchSuggestedID() {
      try {
        const { data } = await authHttp.get(SUGGESTED_ID_URL);

        if (data.suggestedId) setValue('shipmentNoteNumber', data.suggestedId);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('error: ', error);
      }
    }
    async function fetchSuppliers() {
      setIsSupplierLoading(true);
      try {
        const { data } = await authHttp.post(SUPPLIER_GET, {
          pagination: {
            itemsPerPage: 100,
          },
        });

        if (data) setSuppliers(data.suppliers);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      } finally {
        setIsSupplierLoading(false);
      }
    }
    async function fetchAvailableWarehouses() {
      setIsAvailableWarehousesLoading(true);
      try {
        const {
          data: { matches },
        } = await authHttp.post(AVAILABLE_WAREHOUSES_URL, {
          pagination: {
            lastItemSort: null,
            itemsPerPage: 25,
          },
        });
        setWarehouseOptions(
          matches.map((match: WarehouseMatch) => match.warehouse)
        );
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      } finally {
        setIsAvailableWarehousesLoading(false);
      }
    }
    fetchAvailableWarehouses();
    fetchSuppliers();
    fetchSuggestedID();
  }, []);

  const handleSupplierCreated = (newSupplier: Supplier) => {
    setSuppliers((prev) => [...prev, newSupplier]);
    setValue('supplier', {
      label: newSupplier.name,
      value: newSupplier.id,
    });
    setSelectedSupplier(newSupplier);
    if (newSupplier.locations?.length) {
      const firstLoc = newSupplier.locations[0];
      setValue('supplierLocation', {
        label: firstLoc.address,
        value: firstLoc.locationId || '0',
      });
    } else {
      setValue('supplierLocation', null);
    }
  };

  const {
    fields: skuFields,
    append: appendSku,
    remove: removeSku,
  } = useFieldArray({
    control,
    name: 'skuFields',
  });

  return (
    <>
    <FormPage<CreateInboundShipmentNoteFormValues>
      header={header}
      pages={pages}
      methods={methods}
      currentStep={currentStep}
      setCurrentStep={setCurrentStep}
      onSubmit={onSubmit}
      isSubmitting={isSubmitting}
    >
      <Subheader subHeader='Inbound Shipment Note Details' />
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4 mt-2'>
        {skuFields.map((sku, index) => (
          <SkuFieldRow
            key={sku.id}
            sku={sku}
            index={index}
            control={control}
            removeSku={removeSku}
            skuFields={skuFields}
            formState={formState}
            setValue={setValue}
            setError={methods.setError}
            clearErrors={methods.clearErrors}
            supplierId={selectedSupplier?.id}
          />
        ))}
        <div
          className='flex flex-col justify-center items-center border border-dashed border-gray-300 rounded p-4 cursor-pointer min-h-[180px] hover:bg-gray-50 transition'
          onClick={() =>
            appendSku({
              palletising: {
                palletisingId: {
                  label: '',
                  value: '',
                },
              },
              id: '',
              skuId: '',
              skuQuantity: '',
            })
          }
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              appendSku({
                palletising: {
                  palletisingId: {
                    label: '',
                    value: '',
                  },
                },
                id: '',
                skuId: '',
                skuQuantity: '',
              });
            }
          }}
          tabIndex={0}
          role='button'
          aria-label='Add SKU'
        >
          <MdAddCircleOutline className='text-blue-600 mb-2' size={32} />
          <span className='text-gray-600 font-medium'>Add SKU</span>
        </div>
      </div>
      <div className='grid grid-cols-2 gap-x-8 gap-y-4 w-full'>
        <div className='flex flex-col gap-y-4'>
          <Controller
            name='shipmentNoteNumber'
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                label='Shipment Note Number'
                placeholder='Enter shipment note number'
                isInvalid={!!formState.errors.shipmentNoteNumber}
                validationMessage={formState.errors.shipmentNoteNumber?.message}
                sizeVariant='sm'
                grid
              />
            )}
          />
          <Controller
            name='supplier'
            control={control}
            render={({ field }) => (
              <div>
                <Select
                  label='Supplier'
                  name='supplier'
                  options={suppliers.map((supplier) => ({
                    label: supplier.name,
                    value: supplier.id,
                  }))}
                  selected={field.value}
                  setSelected={(option) => {
                    field.onChange(option);
                    const supplier = suppliers.find(
                      (s) => s.id === option?.value
                    );
                    setSelectedSupplier(supplier || null);
                    // Auto-select first location
                    if (supplier?.locations?.length) {
                      const firstLoc = supplier.locations[0];
                      setValue('supplierLocation', {
                        label: firstLoc.address,
                        value: firstLoc.locationId || '0',
                      });
                    } else {
                      setValue('supplierLocation', null);
                    }
                  }}
                  isInvalid={!!formState.errors.supplier}
                  validationMessage={formState.errors.supplier?.message}
                  actionLabel='+ Create New Supplier'
                  onActionClick={() => setIsCreateSupplierModalOpen(true)}
                />
                {selectedSupplier?.locations?.length === 1 && (
                  <p className='mt-1 text-xs text-gray-500'>
                    Address: {selectedSupplier.locations[0].address}
                  </p>
                )}
              </div>
            )}
          />
          {selectedSupplier && selectedSupplier.locations?.length > 1 && (
            <Controller
              name='supplierLocation'
              control={control}
              render={({ field }) => (
                <Select
                  label='Supplier Location'
                  options={selectedSupplier.locations.map((loc, idx) => ({
                    label: loc.address,
                    value: loc.locationId || String(idx),
                  }))}
                  selected={field.value}
                  setSelected={field.onChange}
                />
              )}
            />
          )}
        </div>
        <div className='flex flex-col gap-y-4'>
          <Controller
            name='warehouse'
            control={control}
            render={({ field }) => (
              <Select
                label='Warehouse'
                name='warehouse'
                options={warehouseOptions.map((warehouse) => ({
                  label: warehouse.name,
                  value: warehouse.warehouseId,
                }))}
                selected={field.value}
                setSelected={field.onChange}
                isInvalid={!!formState.errors.warehouse}
                validationMessage={formState.errors.warehouse?.message}
              />
            )}
          />
          <Controller
            name='estimatedTimeOfArrival'
            control={control}
            render={({ field }) => (
              <Datepicker
                label='ETA'
                {...field}
                isInvalid={!!formState.errors.estimatedTimeOfArrival}
                validationMessage={
                  formState.errors.estimatedTimeOfArrival?.message
                }
              />
            )}
          />
        </div>
      </div>
      <div className='w-full'>
        <Controller
          name='comment'
          control={control}
          render={({ field }) => (
            <Textarea
              {...field}
              label='Comment'
              placeholder='Enter any additional comments'
              optional
            />
          )}
        />
      </div>
    </FormPage>
    <CreateSupplierModal
      isOpen={isCreateSupplierModalOpen}
      onClose={() => setIsCreateSupplierModalOpen(false)}
      onSupplierCreated={handleSupplierCreated}
    />
    </>
  );
}
